import { useMemo } from "react";
import { useQuery } from "react-query";

import { Kpi, KpiFormat, KpiType } from "../Alert/Filter/FilterType";
import { checkErrors, getDefaultOptions } from "./Category";
import { GenericDjangoPaginatedResponse } from "./utils";

interface ApiKpi {
  id: number;
  code: string;
  label: string;
  format: KpiFormat;
  order: number;
  kpi_type: { id: number; label: string; rank: number };
}

export interface KpiCatalog {
  /** Every KPI, by type rank then KPI order. */
  kpis: Kpi[];
  /** The KPI types by rank, each with its KPIs in `children`. */
  types: KpiType[];
}

export const listKpiCatalog = async (): Promise<KpiCatalog> => {
  const url = new URL("/api/kpis/", window.origin);
  url.searchParams.append("page_size", "10000");

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  const json: GenericDjangoPaginatedResponse<ApiKpi> = await response.json();

  const rows = [...json.results].sort(
    (a, b) =>
      a.kpi_type.rank - b.kpi_type.rank ||
      a.kpi_type.id - b.kpi_type.id ||
      a.order - b.order ||
      a.id - b.id
  );
  const types = new Map<number, KpiType & { children: Kpi[] }>();
  const kpis = rows.map((row) => {
    const kpi: Kpi = {
      id: row.id,
      name: row.code,
      label: row.label,
      order: row.order,
      format: row.format,
    };
    const type = types.get(row.kpi_type.id) ?? {
      id: row.kpi_type.id,
      label: row.kpi_type.label,
      order: row.kpi_type.rank,
      children: [],
    };
    type.children.push(kpi);
    types.set(type.id, type);
    return kpi;
  });

  return { kpis, types: Array.from(types.values()) };
};

// One request for every KPI picker, filter and column header.
export const useKpiCatalog = () => useQuery("kpi-catalog", listKpiCatalog);

// The catalog's KPIs among `ids`, in catalog order.
export const useKpis = (ids: number[]) => {
  const { data, isLoading, isFetched, isFetching } = useKpiCatalog();
  const idList = ids.join(",");
  const kpis = useMemo(() => {
    const wanted = new Set(idList.split(",").map(Number));
    return data?.kpis.filter((kpi) => wanted.has(kpi.id));
  }, [data, idList]);
  return { data: kpis, isLoading, isFetched, isFetching };
};
